import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  writeBatch,
  Timestamp,
} from "firebase/firestore";
import { db } from "./init";
import { HomeTab, HomeTabConfig, HomeTabType } from "@/types/homeTab";
import { convertTimestampToDate } from "./firestore";

/**
 * Tab ID 驗證規則
 */
export const TAB_ID_REGEX = /^[a-z0-9_]+$/;
export const TAB_ID_MIN_LENGTH = 1;

/**
 * 驗證 Tab ID 格式
 */
export function validateTabId(tabId: string): { valid: boolean; error?: string } {
  if (!tabId || tabId.trim().length < TAB_ID_MIN_LENGTH) {
    return { valid: false, error: "Tab ID 為必填" };
  }
  if (!TAB_ID_REGEX.test(tabId)) {
    return { valid: false, error: "Tab ID 只能包含小寫字母、數字和底線" };
  }
  return { valid: true };
}

/**
 * Firestore HomeTab 數據類型（包含 Timestamp）
 */
interface FirestoreHomeTabData {
  id?: string;
  order?: number;
  title?: string;
  icon?: string;
  enabled?: boolean;
  type?: string;
  config?: {
    tag_ids?: string[];
    drama_ids?: string[];
    limit?: number;
  };
  created_at?: Timestamp | Date | null;
  updated_at?: Timestamp | Date | null;
  createdAt?: Timestamp | Date | null;
  updatedAt?: Timestamp | Date | null;
}

/**
 * 轉換 HomeTab 數據（處理 Timestamp）
 */
function convertHomeTabData(data: FirestoreHomeTabData, id: string): Omit<HomeTab, "id"> {
  return {
    order: data.order ?? 0,
    title: data.title || "",
    icon: data.icon,
    enabled: data.enabled ?? true,
    type: (data.type || "featured") as HomeTabType,
    config: data.config || {},
    created_at: convertTimestampToDate(data.created_at || data.createdAt) as Date | undefined,
    updated_at: convertTimestampToDate(data.updated_at || data.updatedAt) as Date | undefined,
    createdAt: convertTimestampToDate(data.createdAt || data.created_at) as Date | undefined,
    updatedAt: convertTimestampToDate(data.updatedAt || data.updated_at) as Date | undefined,
  };
}

/**
 * 獲取所有 Home Tabs（按 order 排序）
 */
export async function getHomeTabs(): Promise<HomeTab[]> {
  try {
    const tabsRef = collection(db, "home_tabs");
    let querySnapshot;
    let useClientSort = false;

    try {
      const q = query(tabsRef, orderBy("order", "asc"));
      querySnapshot = await getDocs(q);
    } catch (orderError) {
      // 如果 orderBy 失敗（可能沒有索引），使用基本查詢並在客戶端排序
      console.warn("Failed to order by 'order', using basic query with client-side sort:", orderError);
      querySnapshot = await getDocs(query(tabsRef));
      useClientSort = true;
    }

    const tabs = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...convertHomeTabData(doc.data(), doc.id),
    })) as HomeTab[];

    // 如果沒有使用 orderBy，在客戶端排序
    if (useClientSort) {
      tabs.sort((a, b) => a.order - b.order);
    }

    return tabs;
  } catch (error) {
    console.error("Error getting home tabs:", error);
    throw error;
  }
}

/**
 * 獲取單個 Home Tab
 */
export async function getHomeTab(id: string): Promise<HomeTab | null> {
  try {
    const tabRef = doc(db, "home_tabs", id);
    const tabSnap = await getDoc(tabRef);

    if (tabSnap.exists()) {
      return {
        id: tabSnap.id,
        ...convertHomeTabData(tabSnap.data(), tabSnap.id),
      } as HomeTab;
    }

    return null;
  } catch (error) {
    console.error("Error getting home tab:", error);
    throw error;
  }
}

/**
 * 創建 Home Tab
 * 使用 id 作為文檔 ID
 */
export async function createHomeTab(
  data: Omit<HomeTab, "id" | "created_at" | "updated_at" | "createdAt" | "updatedAt"> & { id?: string }
): Promise<string> {
  try {
    // 驗證 id 格式（如果提供了 id）
    if (data.id) {
      const idValidation = validateTabId(data.id);
      if (!idValidation.valid) {
        throw new Error(idValidation.error || "Tab ID 驗證失敗");
      }
    }

    // 驗證必填欄位
    if (!data.title || !data.title.trim()) {
      throw new Error("標題為必填");
    }
    if (data.order === undefined || data.order < 0) {
      throw new Error("順序必須大於等於 0");
    }

    // 檢查 Tab 是否已存在（如果提供了 id）
    let tabId = data.id;
    if (tabId) {
      const existingTab = await getHomeTab(tabId);
      if (existingTab) {
        throw new Error("此 Tab ID 已存在");
      }
    } else {
      // 如果沒有提供 id，生成一個基於標題的 id
      tabId = data.title
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9_]/g, "_")
        .replace(/_+/g, "_")
        .replace(/^_|_$/g, "");
      
      // 如果生成的 id 為空，使用預設值
      if (!tabId || tabId.length === 0) {
        tabId = "tab";
      }
      
      // 確保 id 唯一
      let counter = 1;
      let uniqueId = tabId;
      while (await getHomeTab(uniqueId)) {
        uniqueId = `${tabId}_${counter}`;
        counter++;
        // 防止無限循環
        if (counter > 1000) {
          throw new Error("無法生成唯一的 Tab ID，請手動指定");
        }
      }
      tabId = uniqueId;
    }

    const tabRef = doc(db, "home_tabs", tabId);
    const tabData: {
      id: string;
      order: number;
      title: string;
      icon?: string;
      enabled: boolean;
      type: string;
      config: HomeTabConfig;
      created_at: Timestamp;
      updated_at: Timestamp;
    } = {
      id: tabId,
      order: data.order,
      title: data.title,
      enabled: data.enabled ?? true,
      type: data.type,
      config: data.config || {},
      created_at: Timestamp.now(),
      updated_at: Timestamp.now(),
    };

    if (data.icon) {
      tabData.icon = data.icon;
    }

    await setDoc(tabRef, tabData);
    return tabId;
  } catch (error) {
    console.error("Error creating home tab:", error);
    throw error;
  }
}

/**
 * 更新 Home Tab
 */
export async function updateHomeTab(
  id: string,
  data: Partial<Omit<HomeTab, "id" | "created_at" | "createdAt">>
): Promise<void> {
  try {
    const tabRef = doc(db, "home_tabs", id);
    
    // 驗證必填欄位（如果更新了 title）
    if (data.title !== undefined && (!data.title || !data.title.trim())) {
      throw new Error("標題為必填");
    }
    
    // 驗證 order（如果更新了）
    if (data.order !== undefined && data.order < 0) {
      throw new Error("順序必須大於等於 0");
    }

    const updateData: {
      updated_at: Timestamp;
      order?: number;
      title?: string;
      icon?: string;
      enabled?: boolean;
      type?: string;
      config?: HomeTabConfig;
    } = {
      updated_at: Timestamp.now(),
    };

    // 只更新提供的欄位
    if (data.order !== undefined) updateData.order = data.order;
    if (data.title !== undefined) updateData.title = data.title;
    if (data.icon !== undefined) updateData.icon = data.icon;
    if (data.enabled !== undefined) updateData.enabled = data.enabled;
    if (data.type !== undefined) updateData.type = data.type;
    // config 直接使用提供的值（前端已經處理好合併）
    if (data.config !== undefined) updateData.config = data.config;

    await updateDoc(tabRef, updateData);
  } catch (error) {
    console.error("Error updating home tab:", error);
    throw error;
  }
}

/**
 * 刪除 Home Tab
 */
export async function deleteHomeTab(id: string): Promise<void> {
  try {
    const tabRef = doc(db, "home_tabs", id);
    await deleteDoc(tabRef);
  } catch (error) {
    console.error("Error deleting home tab:", error);
    throw error;
  }
}

/**
 * 批量更新 Home Tabs 順序
 * @param updates - 包含 id 和 order 的更新陣列
 */
export async function reorderHomeTabs(
  updates: Array<{ id: string; order: number }>
): Promise<void> {
  try {
    const batch = writeBatch(db);

    updates.forEach(({ id, order }) => {
      const tabRef = doc(db, "home_tabs", id);
      batch.update(tabRef, {
        order,
        updated_at: Timestamp.now(),
      });
    });

    await batch.commit();
  } catch (error) {
    console.error("Error reordering home tabs:", error);
    throw error;
  }
}

